import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const PendingCredit = () => {
  const [creditSalesData, setCreditSalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [clearingId, setClearingId] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [activeTab, setActiveTab] = useState('PENDING'); // 'PENDING' (default), 'SETTLED', 'ALL'
  const [viewMode, setViewMode] = useState('DOCTOR'); // 'DOCTOR' (default grouped), 'FLAT'
  const [searchQuery, setSearchQuery] = useState('');
  const [toastMessage, setToastMessage] = useState(null);
  const [expandedDoctors, setExpandedDoctors] = useState(new Set());

  // Doctor Customer Statement Modal State
  const [selectedDoctorGroupName, setSelectedDoctorGroupName] = useState(null);
  const [doctorCustomerSearch, setDoctorCustomerSearch] = useState('');

  // Partial Payment Modal State
  const [paymentModalSale, setPaymentModalSale] = useState(null);
  const [payNowAmount, setPayNowAmount] = useState('');
  const [paymentModeInput, setPaymentModeInput] = useState('CASH');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNote, setPaymentNote] = useState('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Edit Credit Modal State
  const [editCreditModalSale, setEditCreditModalSale] = useState(null);
  const [editCustName, setEditCustName] = useState('');
  const [editCustMob, setEditCustMob] = useState('');
  const [editDoctorName, setEditDoctorName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTotalAmount, setEditTotalAmount] = useState('');
  const [editExtraCredit, setEditExtraCredit] = useState('');
  const [editPaidAmount, setEditPaidAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);



  const navigate = useNavigate();

  // Autocomplete helpers
  const knownCustomers = useMemo(() => {
    const map = new Map();
    creditSalesData.forEach(s => {
      if (s.custName && s.custName.trim() && !map.has(s.custName.trim())) {
        map.set(s.custName.trim(), s.custMob || '');
      }
    });
    return Array.from(map.entries()).map(([name, mob]) => ({ name, mob }));
  }, [creditSalesData]);

  const knownDoctors = useMemo(() => {
    const set = new Set();
    creditSalesData.forEach(s => {
      if (s.doctorName && s.doctorName.trim() && s.doctorName !== 'Self / Direct') {
        set.add(s.doctorName.trim());
      }
    });
    return Array.from(set);
  }, [creditSalesData]);

  // Helper to check if current date is in the last week of the month (24th or later)
  const isLastWeekOfMonth = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return today.getDate() >= (lastDay - 7);
  };

  // Helper calculation functions
  const getSaleTotal = (sale) => parseFloat(sale.netAmount || sale.amount || 0) || 0;

  const getSalePaid = (sale) => {
    if (sale.paymentStatus === 'PAID') {
      const p = parseFloat(sale.amountPaid);
      return !isNaN(p) && p > 0 ? p : getSaleTotal(sale);
    }
    const p = parseFloat(sale.amountPaid);
    return !isNaN(p) && p > 0 ? p : 0;
  };

  const getSaleRemaining = (sale) => {
    if (sale.paymentStatus === 'PAID') return 0;
    if (sale.balanceDue !== undefined && sale.balanceDue !== null && !isNaN(parseFloat(sale.balanceDue))) {
      return Math.max(0, parseFloat(sale.balanceDue));
    }
    return Math.max(0, getSaleTotal(sale) - getSalePaid(sale));
  };

  const fetchCreditSales = async () => {
    try {
      let data = null;
      try {
        const res = await fetch(apiUrl('/api/sales'));
        if (res.ok) {
          data = await res.json();
        }
      } catch (e) {
        console.error('Failed to fetch credit sales:', e);
      }
      if (data) {
        // Filter out credit sales or sales with pending balance
        const creditSales = data.filter(sale => 
          sale.saleType === 'CREDIT' || (parseFloat(sale.balanceDue) > 0)
        );
        setCreditSalesData(creditSales);
      }
    } catch (error) {
      console.error('Failed to fetch sales', error);
    } finally {
      setLoadingSales(false);
    }
  };

  useEffect(() => {
    fetchCreditSales();
  }, []);

  // Quick full clear
  const handleClearFee = async (id, custName, remainingAmt) => {
    const sale = creditSalesData.find(s => s.id === id);
    if (!sale) return;
    const total = getSaleTotal(sale);

    if (!window.confirm(`Are you sure you want to mark the remaining credit of ₹${remainingAmt.toFixed(2)} for "${custName || 'Customer'}" as FULLY PAID?`)) return;
    
    setClearingId(id);
    try {
      const payload = {
        paymentStatus: 'PAID',
        paymentMode: sale.paymentMode || 'CASH',
        amountPaid: total,
        balanceDue: 0,
        paymentHistory: [
          ...(sale.paymentHistory || []),
          {
            date: new Date().toISOString().split('T')[0],
            amount: remainingAmt,
            mode: sale.paymentMode || 'CASH',
            note: 'Full settlement marked'
          }
        ]
      };

      let success = false;
      try {
        const res = await fetch(apiUrl(`/api/sales/${id}/clear`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) success = true;
      } catch (e) {
        console.error('Failed to clear credit:', e);
      }

      if (success) {
        setCreditSalesData(prevData => prevData.map(s => 
          s.id === id ? { ...s, ...payload, paymentStatus: 'PAID', clearedAt: new Date().toISOString() } : s
        ));
        setToastMessage(`✓ ₹${remainingAmt.toFixed(2)} for ${custName || 'Customer'} successfully marked as FULLY PAID!`);
        setTimeout(() => setToastMessage(null), 4000);
      } else {
        alert('Failed to clear the credit in database.');
      }
    } catch (error) {
      console.error('Error clearing fee:', error);
      alert('An error occurred while clearing the credit.');
    } finally {
      setClearingId(null);
    }
  };

  // Open Receive Payment Modal (Partial / Full payment)
  const openPaymentModal = (sale, e) => {
    if (e) e.stopPropagation();
    const remaining = getSaleRemaining(sale);
    setPaymentModalSale(sale);
    setPayNowAmount(remaining > 0 ? String(remaining) : '');
    setPaymentModeInput(sale.paymentMode && sale.paymentMode !== 'CREDIT' ? sale.paymentMode : 'CASH');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNote('');
  };

  // Save partial or full payment
  const handleSavePayment = async () => {
    if (!paymentModalSale) return;
    const payVal = parseFloat(payNowAmount) || 0;
    if (payVal <= 0) {
      alert("कृपया योग्य रक्कम भरा (Enter a valid amount greater than 0).");
      return;
    }

    const sale = paymentModalSale;
    const total = getSaleTotal(sale);
    const currentPaid = getSalePaid(sale);
    const currentRemaining = getSaleRemaining(sale);

    if (payVal > currentRemaining) {
      if (!window.confirm(`भरलेली रक्कम (₹${payVal.toFixed(2)}) ही शिल्लक उधारीपेक्षा (₹${currentRemaining.toFixed(2)}) जास्त आहे. पुढे सुरू ठेवायचे का?`)) {
        return;
      }
    }

    const newTotalPaid = Math.min(total, currentPaid + payVal);
    const newRemaining = Math.max(0, currentRemaining - payVal);
    const newStatus = newRemaining === 0 ? 'PAID' : 'PARTIAL';

    const newPaymentRecord = {
      date: paymentDate || new Date().toISOString().split('T')[0],
      amount: payVal,
      mode: paymentModeInput,
      note: paymentNote || ''
    };

    const updatedHistory = [...(sale.paymentHistory || []), newPaymentRecord];

    setIsSubmittingPayment(true);
    try {
      const payload = {
        paymentStatus: newStatus,
        paymentMode: paymentModeInput,
        amountPaid: newTotalPaid,
        balanceDue: newRemaining,
        paymentHistory: updatedHistory,
        paymentNote: paymentNote || ''
      };

      let success = false;
      try {
        const res = await fetch(apiUrl(`/api/sales/${sale.id}/clear`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) success = true;
      } catch (e) {
        console.error('Failed to update payment on backend:', e);
      }

      if (success) {
        setCreditSalesData(prev => prev.map(s => {
          if (s.id === sale.id) {
            return {
              ...s,
              ...payload,
              clearedAt: newStatus === 'PAID' ? new Date().toISOString() : s.clearedAt
            };
          }
          return s;
        }));

        setToastMessage(
          newRemaining === 0
            ? `✓ ${sale.custName || 'Customer'} ची पूर्ण उधारी जमा झाली! (शिल्लक बाकी: ₹0.00)`
            : `✓ ₹${payVal.toFixed(2)} (${paymentModeInput}) जमा झाले! शिल्लक बाकी उधारी: ₹${newRemaining.toFixed(2)}`
        );
        setTimeout(() => setToastMessage(null), 5000);
        setPaymentModalSale(null);

        if (selectedSale && selectedSale.id === sale.id) {
          setSelectedSale({
            ...selectedSale,
            ...payload
          });
        }
      } else {
        alert("Failed to save payment to database.");
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      alert("Error occurred while saving payment.");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleSendReminder = (sale) => {
    if (!sale.custMob) {
      alert('Mobile number not available for this customer.');
      return;
    }
    const total = getSaleTotal(sale).toFixed(2);
    const paid = getSalePaid(sale).toFixed(2);
    const remaining = getSaleRemaining(sale).toFixed(2);
    const doctor = sale.doctorName ? ` (Dr. ${sale.doctorName})` : '';

    let messageText = `नमस्कार ${sale.custName || 'ग्राहक'},\n\nसाई फार्मसी मधून नम्र स्मरणपत्र:\n`;
    if (parseFloat(paid) > 0) {
      messageText += `आपल्या खरेदीचे एकूण बिल: रु. ${total}\nआधी जमा रक्कम: रु. ${paid}\n`;
      messageText += `*सध्या शिल्लक बाकी उधारी: रु. ${remaining}*${doctor}\n`;
    } else {
      messageText += `*आपली शिल्लक बाकी उधारी: रु. ${remaining}* (तारीख: ${sale.date})${doctor}\n`;
    }
    messageText += `\nकृपया लवकरात लवकर शिल्लक रक्कम जमा करावी.\nधन्यवाद!\n- साई फार्मसी`;

    window.open(`https://wa.me/91${sale.custMob}?text=${encodeURIComponent(messageText)}`, '_blank');
  };

  const [isDeletingSale, setIsDeletingSale] = useState(false);

  const handleDeleteCreditSale = async (sale) => {
    if (!sale) return;
    const targetId = sale.id || sale._id || sale.docId || sale.vchNo || sale.invoiceNo;
    const invNo = sale.vchNo || sale.invoiceNo || targetId || '';
    const custName = sale.custName || 'Customer';

    if (!window.confirm(`Are you sure you want to delete credit bill ${invNo ? '#' + invNo : ''} (${custName})? This will permanently remove it from database.`)) {
      return;
    }

    try {
      setIsDeletingSale(true);
      const res = await fetch(apiUrl(`/api/sales/${targetId}`), { method: 'DELETE' });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || errJson.details || 'Failed to delete credit sale');
      }

      // Optimistically update credit sales state
      const targetStr = String(targetId);
      const invStr = String(invNo);
      setCreditSalesData(prev => prev.filter(s => {
        const sId = String(s.id || s._id || s.docId || s.vchNo || '');
        const sInv = String(s.invoiceNo || s.vchNo || '');
        return sId !== targetStr && sInv !== invStr;
      }));
      if (selectedSale && (String(selectedSale.id) === targetStr || String(selectedSale.vchNo) === invStr)) {
        setSelectedSale(null);
      }

      await fetchCreditSales();
      alert(`Credit Bill #${invNo} deleted successfully!`);
    } catch (err) {
      console.error('Delete credit sale error:', err);
      alert('Error deleting credit bill: ' + err.message);
    } finally {
      setIsDeletingSale(false);
    }
  };

  // Open Edit Credit Modal
  const openEditCreditModal = (sale, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setEditCreditModalSale(sale);
    setEditCustName(sale.custName || '');
    setEditCustMob(sale.custMob || '');
    setEditDoctorName(sale.doctorName || '');
    setEditDate(sale.date || new Date().toISOString().split('T')[0]);
    setEditTotalAmount(getSaleTotal(sale).toString());
    setEditExtraCredit('');
    setEditPaidAmount(getSalePaid(sale).toString());
    setEditNote(sale.narration || sale.paymentNote || '');
  };

  // Edit bill in full Sales screen
  const handleEditInSales = (sale) => {
    navigate('/dashboard/sales', { state: { editBill: sale } });
  };

  // Save changes from Edit Credit Modal
  const handleSaveEditCredit = async (e) => {
    if (e) e.preventDefault();
    if (!editCreditModalSale) return;

    const baseTotal = parseFloat(editTotalAmount) || 0;
    const extraCredit = parseFloat(editExtraCredit) || 0;
    const finalTotal = Math.max(0, baseTotal + extraCredit);
    const paidVal = Math.max(0, parseFloat(editPaidAmount) || 0);
    const finalPaid = Math.min(paidVal, finalTotal);
    const finalRemaining = Math.max(0, finalTotal - finalPaid);
    const finalStatus = finalRemaining === 0 ? 'PAID' : (finalPaid > 0 ? 'PARTIAL' : 'PENDING');

    if (finalTotal <= 0 && !editCustName.trim()) {
      alert('कृपया ग्राहकाचे नाव किंवा योग्य रक्कम टाका.');
      return;
    }

    setIsSubmittingEdit(true);

    try {
      const updatedData = {
        ...editCreditModalSale,
        custName: editCustName.trim() || editCreditModalSale.custName || 'Walk-in Customer',
        custMob: editCustMob.trim(),
        doctorName: editDoctorName.trim() || 'Self / Direct',
        date: editDate,
        netAmount: finalTotal,
        amount: finalTotal,
        amountPaid: finalPaid,
        balanceDue: finalRemaining,
        paymentStatus: finalStatus,
        saleType: 'CREDIT',
        paymentMode: finalStatus === 'PAID' ? (editCreditModalSale.paymentMode && editCreditModalSale.paymentMode !== 'CREDIT' ? editCreditModalSale.paymentMode : 'CASH') : 'CREDIT',
        narration: editNote.trim() || editCreditModalSale.narration || ''
      };

      if (extraCredit > 0) {
        const historyEntry = {
          date: new Date().toISOString().split('T')[0],
          time: new Date().toLocaleTimeString(),
          type: 'CREDIT_ADDED',
          amount: extraCredit,
          note: `आणखी उधारी जोडली (+₹${extraCredit.toFixed(2)})` + (editNote ? ` - ${editNote}` : '')
        };
        const prevHistory = Array.isArray(editCreditModalSale.paymentHistory) ? editCreditModalSale.paymentHistory : [];
        updatedData.paymentHistory = [...prevHistory, historyEntry];
      }

      const res = await fetch(apiUrl(`/api/sales/${editCreditModalSale.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });

      if (!res.ok) {
        throw new Error('Failed to update credit bill');
      }

      const savedResult = await res.json();

      setCreditSalesData(prev => prev.map(s => {
        if (s.id === editCreditModalSale.id) {
          return { ...s, ...updatedData, ...savedResult };
        }
        return s;
      }));

      if (selectedSale && selectedSale.id === editCreditModalSale.id) {
        setSelectedSale({ ...selectedSale, ...updatedData, ...savedResult });
      }

      setEditCreditModalSale(null);
      setToastMessage(`क्रेडिट यशस्वीरीत्या अपडेट केले! (बाकी उधारी: ₹${finalRemaining.toFixed(2)})`);
      setTimeout(() => setToastMessage(null), 3500);
    } catch (err) {
      console.error('Error updating credit sale:', err);
      alert('क्रेडिट अपडेट करताना त्रुटी आली: ' + err.message);
    } finally {
      setIsSubmittingEdit(false);
    }
  };



  // Computations
  const pendingSales = creditSalesData.filter(sale => getSaleRemaining(sale) > 0 && sale.paymentStatus !== 'PAID');
  const settledSales = creditSalesData.filter(sale => sale.paymentStatus === 'PAID' || getSaleRemaining(sale) === 0);

  const totalPendingAmount = pendingSales.reduce((acc, sale) => acc + getSaleRemaining(sale), 0);
  const totalSettledAmount = creditSalesData.reduce((acc, sale) => acc + getSalePaid(sale), 0);
  const totalCreditRecorded = creditSalesData.reduce((acc, sale) => acc + getSaleTotal(sale), 0);

  // Filter based on selected Tab and Search Query
  const displayedSales = (activeTab === 'PENDING' ? pendingSales : activeTab === 'SETTLED' ? settledSales : creditSalesData).filter(sale => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (sale.custName && sale.custName.toLowerCase().includes(q)) ||
      (sale.custMob && sale.custMob.includes(q)) ||
      (sale.doctorName && sale.doctorName.toLowerCase().includes(q)) ||
      (sale.paymentMode && sale.paymentMode.toLowerCase().includes(q)) ||
      (sale.vchNo && String(sale.vchNo).toLowerCase().includes(q)) ||
      (sale.date && sale.date.includes(q))
    );
  });

  // Group by Doctor
  const doctorGroups = useMemo(() => {
    const map = {};
    displayedSales.forEach(sale => {
      const rawDoc = (sale.doctorName || '').trim();
      const docKey = rawDoc ? rawDoc : 'Direct / Self / No Doctor';
      if (!map[docKey]) {
        map[docKey] = {
          doctorName: docKey,
          isSelf: !rawDoc,
          doctorArea: sale.doctorArea || '',
          doctorMob: sale.doctorMob || '',
          sales: []
        };
      }
      map[docKey].sales.push(sale);
    });

    const groups = Object.values(map).map(group => {
      const groupTotal = group.sales.reduce((sum, s) => sum + getSaleTotal(s), 0);
      const groupPaid = group.sales.reduce((sum, s) => sum + getSalePaid(s), 0);
      const groupRemaining = group.sales.reduce((sum, s) => sum + getSaleRemaining(s), 0);
      return {
        ...group,
        totalCredit: groupTotal,
        totalPaid: groupPaid,
        totalRemaining: groupRemaining
      };
    });

    return groups.sort((a, b) => {
      if (a.isSelf) return 1;
      if (b.isSelf) return -1;
      return a.doctorName.localeCompare(b.doctorName);
    });
  }, [displayedSales]);

  // Expand all by default whenever doctor groups change initially
  useEffect(() => {
    setExpandedDoctors(new Set(doctorGroups.map(g => g.doctorName)));
  }, [doctorGroups.length]);

  const toggleDoctorExpand = (docName) => {
    setExpandedDoctors(prev => {
      const next = new Set(prev);
      if (next.has(docName)) next.delete(docName);
      else next.add(docName);
      return next;
    });
  };

  const expandAllDoctors = () => setExpandedDoctors(new Set(doctorGroups.map(g => g.doctorName)));
  const collapseAllDoctors = () => setExpandedDoctors(new Set());

  // Active Doctor Group for the Doctor Popup
  const activeDoctorGroup = useMemo(() => {
    if (!selectedDoctorGroupName) return null;
    return doctorGroups.find(g => g.doctorName === selectedDoctorGroupName) || null;
  }, [selectedDoctorGroupName, doctorGroups]);

  // Filtered sales inside Doctor Modal
  const activeDoctorFilteredSales = useMemo(() => {
    if (!activeDoctorGroup) return [];
    if (!doctorCustomerSearch.trim()) return activeDoctorGroup.sales;
    const q = doctorCustomerSearch.toLowerCase().trim();
    return activeDoctorGroup.sales.filter(s =>
      (s.custName && s.custName.toLowerCase().includes(q)) ||
      (s.custMob && s.custMob.includes(q)) ||
      (s.vchNo && String(s.vchNo).toLowerCase().includes(q)) ||
      (s.date && s.date.includes(q))
    );
  }, [activeDoctorGroup, doctorCustomerSearch]);

  // Total addition calculation for the Doctor Modal
  const activeDoctorTotals = useMemo(() => {
    if (!activeDoctorFilteredSales) return { total: 0, paid: 0, remaining: 0 };
    return activeDoctorFilteredSales.reduce(
      (acc, s) => ({
        total: acc.total + getSaleTotal(s),
        paid: acc.paid + getSalePaid(s),
        remaining: acc.remaining + getSaleRemaining(s)
      }),
      { total: 0, paid: 0, remaining: 0 }
    );
  }, [activeDoctorFilteredSales]);

  // Print Doctor Credit Statement
  const handlePrintDoctorStatement = (group, filteredSales, totals) => {
    if (!group) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("कृपया पॉपअप परवानगी द्या (Please allow popups).");
      return;
    }

    const rowsHtml = (filteredSales || []).map((s, idx) => {
      const tot = getSaleTotal(s);
      const pd = getSalePaid(s);
      const rem = getSaleRemaining(s);
      return `
        <tr>
          <td style="padding: 6px; border: 1px solid #cbd5e1; text-align: center;">${idx + 1}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1;">${s.date} (Inv #${s.vchNo || '-'})</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1; font-weight: bold;">${s.custName || 'Walk-in'}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1;">${s.custMob || '-'}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1; text-align: center;">${s.paymentMode || 'CREDIT'}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1; text-align: right;">₹${tot.toFixed(2)}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1; text-align: right; color: #047857;">₹${pd.toFixed(2)}</td>
          <td style="padding: 6px; border: 1px solid #cbd5e1; text-align: right; font-weight: bold; color: #b91c1c;">₹${rem.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Doctor Credit Statement - ${group.doctorName}</title>
        <style>
          body { font-family: sans-serif; padding: 20px; color: #1e293b; font-size: 12px; }
          .header { border-bottom: 2px solid #0f766e; padding-bottom: 10px; margin-bottom: 15px; display: flex; justify-content: space-between; align-items: flex-start; }
          .title { font-size: 18px; font-weight: bold; color: #0f766e; }
          .doc-info { margin-top: 5px; font-size: 13px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th { background-color: #f1f5f9; padding: 8px; border: 1px solid #cbd5e1; text-align: left; font-size: 11px; }
          @media print { button { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">साई फार्मसी (Sai Pharmacy) - डॉक्टर उधारी रजिस्टर</div>
            <div class="doc-info">👨‍⚕️ ${group.isSelf ? 'Direct / Walk-in Bills' : 'Dr. ' + group.doctorName}</div>
            <div style="color: #64748b;">${group.doctorArea ? 'एरिया: ' + group.doctorArea + ' | ' : ''}${group.doctorMob ? 'मोबाईल: ' + group.doctorMob : ''}</div>
          </div>
          <div style="text-align: right; font-size: 11px; color: #64748b;">
            तारीख: ${new Date().toLocaleDateString('mr-IN')}<br>
            एकूण ग्राहक बिले: ${(filteredSales || []).length}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">क्र.</th>
              <th>दिनांक / बिल नं</th>
              <th>ग्राहक नाव (Customer)</th>
              <th>मोबाईल</th>
              <th style="text-align: center;">पेमेंट मोड</th>
              <th style="text-align: right;">एकूण बिल</th>
              <th style="text-align: right;">जमा रक्कम</th>
              <th style="text-align: right;">शिल्लक बाकी</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
          <tfoot>
            <tr style="background: #f8fafc; font-weight: bold; font-size: 13px;">
              <td colspan="5" style="padding: 8px; border: 1px solid #cbd5e1; text-align: right;">एकूण बेरीज (TOTAL ADDITION):</td>
              <td style="padding: 8px; border: 1px solid #cbd5e1; text-align: right;">₹${totals.total.toFixed(2)}</td>
              <td style="padding: 8px; border: 1px solid #cbd5e1; text-align: right; color: #047857;">₹${totals.paid.toFixed(2)}</td>
              <td style="padding: 8px; border: 1px solid #cbd5e1; text-align: right; color: #b91c1c;">₹${totals.remaining.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        <div style="margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; color: #64748b;">
          <div>टीप: वरील हिशोब साई फार्मसीच्या रेकॉर्डनुसार आहे.</div>
          <div>स्वाक्षरी (Signature)</div>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Payment Mode Badge Formatter
  const renderPaymentModeBadge = (mode, isPaid, isPartial) => {
    const normalized = (mode || '').toUpperCase();
    if (normalized.includes('CASH')) {
      return (
        <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1 w-fit">
          <span>💵</span> CASH
        </span>
      );
    }
    if (normalized.includes('ONLINE') || normalized.includes('UPI')) {
      return (
        <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-sky-100 text-sky-800 border border-sky-200 flex items-center gap-1 w-fit">
          <span>📱</span> ONLINE (UPI)
        </span>
      );
    }
    if (normalized.includes('CARD')) {
      return (
        <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-1 w-fit">
          <span>💳</span> CARD
        </span>
      );
    }
    if (normalized.includes('CHEQUE')) {
      return (
        <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-indigo-100 text-indigo-800 border border-indigo-200 flex items-center gap-1 w-fit">
          <span>🏦</span> CHEQUE
        </span>
      );
    }
    if (normalized.includes('MIX')) {
      return (
        <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1 w-fit">
          <span>🔀</span> MIX
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-lg text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-200 flex items-center gap-1 w-fit">
        <span>⏳</span> CREDIT (उधारी)
      </span>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6 animate-in fade-in duration-700 p-2 md:p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[300] bg-emerald-800 text-white px-5 py-3 rounded-2xl shadow-2xl font-bold text-xs flex items-center gap-2 animate-in slide-in-from-top duration-300 border border-emerald-600">
          <svg className="w-5 h-5 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-2 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Back to Dashboard"
          >
            <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div className="w-2 h-8 md:h-10 rounded-full bg-rose-500 shadow-sm"></div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800 tracking-wide flex items-center gap-2">
              <span>Pending Credit (उधारी रजिस्टर)</span>
              <span className="px-2.5 py-0.5 bg-rose-100 text-rose-700 text-xs font-black rounded-full">
                {pendingSales.length} Active Dues
              </span>
            </h2>
            <p className="text-xs text-slate-500 font-medium">Customer Credit Register • डॉक्टरनुसार उधारी, जमा व शिल्लक बाकी नोंद</p>
          </div>
        </div>

        {/* View Mode & Tab Switcher */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Doctor Group vs Flat view */}
          <div className="bg-slate-200/80 p-1 rounded-2xl flex items-center gap-1 border border-slate-300/60 shadow-xs">
            <button
              onClick={() => setViewMode('DOCTOR')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                viewMode === 'DOCTOR' ? 'bg-[#1e5c5c] text-white shadow-sm' : 'text-slate-600 hover:bg-white/50'
              }`}
              title="Group customers under Doctor name"
            >
              <span>👨‍⚕️</span> Doctor-wise Group
            </button>
            <button
              onClick={() => setViewMode('FLAT')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                viewMode === 'FLAT' ? 'bg-[#1e5c5c] text-white shadow-sm' : 'text-slate-600 hover:bg-white/50'
              }`}
              title="View all bills as flat list"
            >
              <span>📋</span> All Bills List
            </button>
          </div>

          {/* Tab Switcher */}
          <div className="bg-slate-200/80 p-1 rounded-2xl flex items-center gap-1 border border-slate-300/60 shadow-xs">
            <button
              onClick={() => setActiveTab('PENDING')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'PENDING' ? 'bg-rose-600 text-white shadow-md' : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-300"></span>
              Pending ({pendingSales.length})
            </button>

            <button
              onClick={() => setActiveTab('SETTLED')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'SETTLED' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-300"></span>
              Cleared ({settledSales.length})
            </button>

            <button
              onClick={() => setActiveTab('ALL')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                activeTab === 'ALL' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              All ({creditSalesData.length})
            </button>
          </div>


        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Active Pending Due */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">Total Active Pending Due (शिल्लक बाकी)</span>
            <h3 className="text-2xl font-black text-rose-700 mt-1">₹{totalPendingAmount.toFixed(2)}</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">{pendingSales.length} Customers pending balance</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
        </div>

        {/* Total Cleared / Settled */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Total Cleared / Collected (जमा रक्कम)</span>
            <h3 className="text-2xl font-black text-emerald-700 mt-1">₹{totalSettledAmount.toFixed(2)}</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">{settledSales.length} Fully cleared / partial collected</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
        </div>

        {/* Total Credit Invoiced */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Credit Invoiced (एकूण उधारी बिल)</span>
            <h3 className="text-2xl font-black text-slate-800 mt-1">₹{totalCreditRecorded.toFixed(2)}</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">{creditSalesData.length} Total credit invoices</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center border border-slate-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
          </div>
        </div>
      </div>

      {/* End of Month Banner */}
      {isLastWeekOfMonth() && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-2xl flex items-center gap-3 shadow-sm animate-pulse">
          <div className="p-2 bg-amber-100 rounded-xl text-amber-600 shrink-0">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
          <div>
            <h3 className="font-bold text-amber-900 text-xs sm:text-sm">End of Month Reminder (महिन्याचा शेवट)</h3>
            <p className="text-[11px] text-amber-800 font-medium">महिन्याचा शेवटचा आठवडा आहे. शिल्लक बाकी उधारीसाठी WhatsApp आयकॉनवर क्लिक करून एका क्लिकवर स्मरणपत्र (Reminder) पाठवा!</p>
          </div>
        </div>
      )}

      {/* Search and Action Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="bg-white rounded-2xl p-3 border border-slate-200 shadow-sm flex items-center gap-2 flex-1 w-full">
          <svg className="w-4 h-4 text-slate-400 ml-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by customer name, mobile, doctor name, bill no, or payment mode..."
            className="w-full text-xs font-bold text-slate-800 bg-transparent outline-none placeholder-slate-400"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600 font-bold text-xs px-2">×</button>
          )}
        </div>

        {viewMode === 'DOCTOR' && doctorGroups.length > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={expandAllDoctors}
              className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 shadow-xs transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAllDoctors}
              className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 shadow-xs transition-colors"
            >
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {loadingSales ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-200 shadow-sm flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
        </div>
      ) : displayedSales.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-200 shadow-sm text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 mb-3">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <p className="text-slate-800 font-bold text-base">
            {activeTab === 'PENDING' ? 'कोणतीही उधारी बाकी नाही! (No pending credits)' : activeTab === 'SETTLED' ? 'No cleared credits found.' : 'No credit sales found.'}
          </p>
          <p className="text-slate-400 text-xs mt-1">
            {activeTab === 'PENDING' ? 'सर्व ग्राहकांची उधारी पूर्ण जमा व क्लिअर झालेली आहे.' : 'Try changing tab filters or search query.'}
          </p>
        </div>
      ) : viewMode === 'DOCTOR' ? (
        /* DOCTOR-WISE GROUPED VIEW */
        <div className="space-y-4">
          {doctorGroups.map((group, gIdx) => {
            const isExpanded = expandedDoctors.has(group.doctorName);
            const isSelf = group.isSelf;

            return (
              <div 
                key={gIdx} 
                className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-200"
              >
                {/* Doctor Group Header Card */}
                <div 
                  onClick={() => toggleDoctorExpand(group.doctorName)}
                  className={`p-4 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-3 transition-colors ${
                    isExpanded ? 'bg-gradient-to-r from-teal-50/70 via-slate-50 to-white border-b border-slate-200' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg shrink-0 shadow-xs ${
                      isSelf ? 'bg-slate-100 text-slate-600 border border-slate-200' : 'bg-[#1e5c5c]/10 text-[#1e5c5c] border border-[#1e5c5c]/20'
                    }`}>
                      {isSelf ? '👤' : '👨‍⚕️'}
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-800 flex items-center gap-2">
                        <span>{isSelf ? 'Direct / Walk-in / Self (डॉक्टर नसलेली बिले)' : `Dr. ${group.doctorName}`}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-teal-100 text-teal-800">
                          {group.sales.length} Bills
                        </span>
                      </h3>
                      {(group.doctorArea || group.doctorMob) && (
                        <p className="text-xs font-bold text-slate-500 mt-0.5 flex items-center gap-2">
                          {group.doctorArea && <span>📍 {group.doctorArea}</span>}
                          {group.doctorMob && <span>📞 {group.doctorMob}</span>}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 self-end md:self-auto">
                    <div className="flex items-center gap-3 text-right">
                      <div>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Invoiced</span>
                        <span className="text-xs font-bold text-slate-700">₹{group.totalCredit.toFixed(2)}</span>
                      </div>
                      <div className="w-px h-6 bg-slate-200"></div>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-emerald-600 block">Total Paid (जमा)</span>
                        <span className="text-xs font-black text-emerald-700">₹{group.totalPaid.toFixed(2)}</span>
                      </div>
                      <div className="w-px h-6 bg-slate-200"></div>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-rose-600 block">Pending Due (बाकी)</span>
                        <span className="text-sm font-black text-rose-700">₹{group.totalRemaining.toFixed(2)}</span>
                      </div>
                    </div>

                    <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDoctorGroupName(group.doctorName);
                          setDoctorCustomerSearch('');
                        }}
                        className="px-3 py-1.5 bg-[#1e5c5c] hover:bg-[#154242] active:scale-95 text-white rounded-xl text-xs font-black shadow-xs flex items-center gap-1.5 transition-all hover:shadow-md cursor-pointer shrink-0"
                        title="या डॉक्टरच्या सर्व ग्राहकांचा एकत्रित पॉपअप उघडा"
                      >
                        <span>👁️ डॉक्टर उधारी पॉपअप</span>
                      </button>

                      <div className={`p-1.5 rounded-full transition-transform duration-200 ${isExpanded ? 'rotate-180 bg-slate-200/70 text-slate-700' : 'bg-slate-100 text-slate-500'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                  </div>
                </div>

                {/* Expanded Customer Sales List under this Doctor */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                        <tr className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider font-bold border-b border-slate-200">
                          <th className="p-3 pl-6">Date / Bill</th>
                          <th className="p-3">Customer Name</th>
                          <th className="p-3">Mobile No</th>
                          <th className="p-3">Payment Mode</th>
                          <th className="p-3 text-right">Total Bill</th>
                          <th className="p-3 text-right">Paid (जमा)</th>
                          <th className="p-3 text-right">Remaining Due (बाकी)</th>
                          <th className="p-3 text-center">Status</th>
                          <th className="p-3 pr-6 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {group.sales.map((sale) => {
                          const totalAmt = getSaleTotal(sale);
                          const paidAmt = getSalePaid(sale);
                          const remainingAmt = getSaleRemaining(sale);
                          const isFullyPaid = sale.paymentStatus === 'PAID' || remainingAmt === 0;
                          const isPartial = !isFullyPaid && paidAmt > 0;

                          return (
                            <tr 
                              key={sale.id} 
                              className={`transition-colors cursor-pointer group ${
                                isFullyPaid ? 'bg-emerald-50/20 hover:bg-emerald-50/50' : isPartial ? 'bg-amber-50/20 hover:bg-amber-50/50' : 'hover:bg-rose-50/30'
                              }`} 
                              onClick={() => setSelectedSale(sale)}
                            >
                              <td className="p-3 pl-6">
                                <div className="text-[11px] font-black text-slate-700">{sale.date}</div>
                                {sale.vchNo && <div className="text-[9px] text-slate-400 font-bold">#{sale.vchNo}</div>}
                              </td>

                              <td className="p-3">
                                <div className={`font-black text-xs ${isFullyPaid ? 'text-emerald-900' : 'text-slate-800'}`}>
                                  {sale.custName || 'Walk-in Customer'}
                                </div>
                                {sale.items?.length > 0 && (
                                  <div className="text-[10px] text-slate-400 font-bold mt-0.5 truncate max-w-[180px]">
                                    {sale.items[0]?.productName} {sale.items.length > 1 ? `+${sale.items.length - 1} more` : ''}
                                  </div>
                                )}
                              </td>

                              <td className="p-3 font-bold text-xs text-slate-600">
                                {sale.custMob || '-'}
                              </td>

                              <td className="p-3">
                                {renderPaymentModeBadge(sale.paymentMode, isFullyPaid, isPartial)}
                              </td>

                              <td className="p-3 text-right font-bold text-xs text-slate-700">
                                ₹{totalAmt.toFixed(2)}
                              </td>

                              <td className="p-3 text-right font-black text-xs text-emerald-700">
                                {paidAmt > 0 ? `₹${paidAmt.toFixed(2)}` : '₹0.00'}
                              </td>

                              <td className="p-3 text-right">
                                {isFullyPaid ? (
                                  <span className="text-xs font-black text-emerald-600">₹0.00</span>
                                ) : (
                                  <span className="text-sm font-black text-rose-700">₹{remainingAmt.toFixed(2)}</span>
                                )}
                              </td>

                              <td className="p-3 text-center">
                                {isFullyPaid ? (
                                  <span className="text-emerald-800 bg-emerald-100 border border-emerald-200 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                                    ✓ PAID
                                  </span>
                                ) : isPartial ? (
                                  <span className="text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full text-[9px] font-black">
                                    PARTIAL (अर्धवट)
                                  </span>
                                ) : (
                                  <span className="text-rose-800 bg-rose-100 border border-rose-200 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                                    PENDING
                                  </span>
                                )}
                              </td>

                              <td className="p-3 pr-6 text-center">
                                <div className="flex justify-center items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={(e) => openEditCreditModal(sale, e)}
                                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-600 text-amber-900 hover:text-white border border-amber-300 rounded-lg text-[10px] font-black transition-all flex items-center gap-1 shadow-2xs active:scale-95 cursor-pointer whitespace-nowrap"
                                    title="क्रेडिट तपशील एडिट करा किंवा आणखी उधारी जोडा (Credit Edit)"
                                  >
                                    <span>✏️</span> क्रेडिट एडिट
                                  </button>
                                  {!isFullyPaid && (
                                    <>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleSendReminder(sale); }}
                                        className={`p-1.5 rounded-xl transition-all ${isLastWeekOfMonth() ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                        title="WhatsApp Reminder (शिल्लक बाकी स्मरणपत्र)"
                                      >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.666.598 1.216.771 1.391.858.175.087.277.072.378-.043.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.418-.099.824z"></path></svg>
                                      </button>

                                      <button
                                        onClick={(e) => openPaymentModal(sale, e)}
                                        className="px-2.5 py-1 bg-teal-700 hover:bg-teal-800 text-white text-[10px] font-black rounded-lg transition-all flex items-center gap-1 shadow-sm active:scale-95"
                                        title="Record partial or full payment"
                                      >
                                        <span>₹</span> जमा करा (Pay)
                                      </button>

                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleClearFee(sale.id, sale.custName, remainingAmt); }}
                                        disabled={clearingId === sale.id}
                                        className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black rounded-lg transition-all shadow-sm active:scale-95"
                                        title="Mark remaining balance as fully paid"
                                      >
                                        Full Paid
                                      </button>
                                    </>
                                  )}
                                  {isFullyPaid && (
                                    <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-lg text-[9px] font-bold">
                                      Cleared
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteCreditSale(sale); }}
                                    disabled={isDeletingSale}
                                    className="p-1.5 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border border-rose-200 transition-all cursor-pointer shadow-2xs"
                                    title="Delete this credit bill"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* FLAT TABLE VIEW (All bills list) */
        <div className="bg-white rounded-3xl p-3 md:p-6 border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold border-b border-slate-200">
                <th className="p-3 rounded-tl-xl">Date / Bill</th>
                <th className="p-3">Customer Name</th>
                <th className="p-3">Doctor</th>
                <th className="p-3">Mobile No</th>
                <th className="p-3">Payment Mode</th>
                <th className="p-3 text-right">Total Bill</th>
                <th className="p-3 text-right">Paid (जमा)</th>
                <th className="p-3 text-right">Remaining Due</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center rounded-tr-xl">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedSales.map((sale) => {
                const totalAmt = getSaleTotal(sale);
                const paidAmt = getSalePaid(sale);
                const remainingAmt = getSaleRemaining(sale);
                const isFullyPaid = sale.paymentStatus === 'PAID' || remainingAmt === 0;
                const isPartial = !isFullyPaid && paidAmt > 0;

                return (
                  <tr 
                    key={sale.id} 
                    className={`transition-colors cursor-pointer group ${
                      isFullyPaid ? 'bg-emerald-50/30 hover:bg-emerald-50' : isPartial ? 'bg-amber-50/30 hover:bg-amber-50' : 'hover:bg-rose-50/30'
                    }`} 
                    onClick={() => setSelectedSale(sale)}
                  >
                    <td className="p-3">
                      <div className="text-[11px] font-black text-slate-700">{sale.date}</div>
                      {sale.vchNo && <div className="text-[9px] text-slate-400 font-bold">#{sale.vchNo}</div>}
                    </td>

                    <td className="p-3">
                      <div className={`font-black text-xs ${isFullyPaid ? 'text-emerald-900' : 'text-slate-800'}`}>
                        {sale.custName || 'Walk-in Customer'}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="font-bold text-xs text-slate-700 flex items-center gap-1">
                        <span className="text-slate-400">👨‍⚕️</span>
                        <span>{sale.doctorName || 'Self / Direct'}</span>
                      </div>
                    </td>

                    <td className="p-3 font-bold text-xs text-slate-600">
                      {sale.custMob || '-'}
                    </td>

                    <td className="p-3">
                      {renderPaymentModeBadge(sale.paymentMode, isFullyPaid, isPartial)}
                    </td>

                    <td className="p-3 text-right font-bold text-xs text-slate-700">
                      ₹{totalAmt.toFixed(2)}
                    </td>

                    <td className="p-3 text-right font-black text-xs text-emerald-700">
                      ₹{paidAmt.toFixed(2)}
                    </td>

                    <td className="p-3 text-right">
                      {isFullyPaid ? (
                        <span className="text-xs font-black text-emerald-600">₹0.00</span>
                      ) : (
                        <span className="text-sm font-black text-rose-700">₹{remainingAmt.toFixed(2)}</span>
                      )}
                    </td>

                    <td className="p-3 text-center">
                      {isFullyPaid ? (
                        <span className="text-emerald-800 bg-emerald-100 border border-emerald-200 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                          ✓ PAID
                        </span>
                      ) : isPartial ? (
                        <span className="text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full text-[9px] font-black">
                          PARTIAL
                        </span>
                      ) : (
                        <span className="text-rose-800 bg-rose-100 border border-rose-200 px-2.5 py-0.5 rounded-full text-[9px] font-black">
                          PENDING
                        </span>
                      )}
                    </td>

                    <td className="p-3 text-center">
                      <div className="flex justify-center items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => openEditCreditModal(sale, e)}
                          className="px-2.5 py-1 bg-amber-50 hover:bg-amber-600 text-amber-900 hover:text-white border border-amber-300 rounded-lg text-[10px] font-black transition-all flex items-center gap-1 shadow-2xs active:scale-95 cursor-pointer whitespace-nowrap"
                          title="क्रेडिट तपशील एडिट करा किंवा आणखी उधारी जोडा (Credit Edit)"
                        >
                          <span>✏️</span> क्रेडिट एडिट
                        </button>
                        {!isFullyPaid && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSendReminder(sale); }}
                              className="p-1.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200"
                              title="Send Reminder on WhatsApp"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.666.598 1.216.771 1.391.858.175.087.277.072.378-.043.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.418-.099.824z"></path></svg>
                            </button>
                            <button
                              onClick={(e) => openPaymentModal(sale, e)}
                              className="px-2.5 py-1 bg-teal-700 hover:bg-teal-800 text-white text-[10px] font-black rounded-lg transition-all shadow-sm active:scale-95"
                            >
                              जमा करा (Pay)
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClearFee(sale.id, sale.custName, remainingAmt); }}
                              disabled={clearingId === sale.id}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black rounded-lg transition-all shadow-sm active:scale-95"
                            >
                              Full Paid
                            </button>
                          </>
                        )}
                        {isFullyPaid && (
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-lg text-[9px] font-bold">
                            Cleared
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCreditSale(sale); }}
                          disabled={isDeletingSale}
                          className="p-1.5 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border border-rose-200 transition-all cursor-pointer shadow-2xs"
                          title="Delete this credit bill"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* DOCTOR CUSTOMERS & CREDIT STATEMENT MODAL (एकाच डॉक्टरच्या सर्व ग्राहकांचा पॉपअप व एकूण बेरीज) */}
      {activeDoctorGroup && (
        <div 
          className="fixed inset-0 z-[160] flex items-center justify-center p-2 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
          onMouseDown={() => setSelectedDoctorGroupName(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[92vh] overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200"
            onMouseDown={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-teal-900 via-slate-900 to-teal-950 text-white flex justify-between items-center shrink-0 border-b border-teal-700/40 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center text-2xl shadow-inner">
                  {activeDoctorGroup.isSelf ? '👤' : '👨‍⚕️'}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg sm:text-xl font-black text-white">
                      {activeDoctorGroup.isSelf ? 'Direct / Walk-in Bills (डॉक्टर नसलेली बिले)' : `Dr. ${activeDoctorGroup.doctorName}`}
                    </h2>
                    <span className="px-2 py-0.5 rounded-full text-xs font-black bg-teal-500/30 text-teal-200 border border-teal-400/30">
                      {activeDoctorFilteredSales.length} ग्राहकांची बिले
                    </span>
                  </div>
                  <p className="text-xs text-teal-200/80 font-bold mt-0.5 flex items-center gap-3">
                    {activeDoctorGroup.doctorArea && <span>📍 एरिया: {activeDoctorGroup.doctorArea}</span>}
                    {activeDoctorGroup.doctorMob && <span>📞 फोन: {activeDoctorGroup.doctorMob}</span>}
                    <span>• सर्व ग्राहक व उधारी हिशोब</span>
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handlePrintDoctorStatement(activeDoctorGroup, activeDoctorFilteredSales, activeDoctorTotals)}
                  className="px-3 py-1.5 bg-teal-800/80 hover:bg-teal-700 text-teal-100 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 border border-teal-600/50 cursor-pointer shadow-xs"
                  title="प्रिंट किंवा PDF काढा"
                >
                  <span>🖨️ प्रिंट स्टेटमेंट</span>
                </button>
                <button 
                  onClick={() => setSelectedDoctorGroupName(null)}
                  className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>

            {/* Addition Summary KPI Cards (एकूण बेरीज) */}
            <div className="p-4 bg-slate-50 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-3 shrink-0">
              {/* Total Invoiced Addition */}
              <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg font-black shrink-0">
                  📋
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                    एकूण उधारी बेरीज (Total Credit)
                  </span>
                  <span className="text-lg font-black text-slate-800">
                    ₹{activeDoctorTotals.total.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-slate-500 block font-medium">सर्व बिलांची एकूण बेरीज</span>
                </div>
              </div>

              {/* Total Paid Addition */}
              <div className="bg-white p-3 rounded-2xl border border-emerald-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg font-black shrink-0">
                  💰
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-emerald-600 block tracking-wider">
                    एकूण जमा बेरीज (Total Paid)
                  </span>
                  <span className="text-lg font-black text-emerald-700">
                    ₹{activeDoctorTotals.paid.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-emerald-600/70 block font-medium">आजवर जमा झालेली एकूण रक्कम</span>
                </div>
              </div>

              {/* Total Remaining Addition */}
              <div className="bg-white p-3 rounded-2xl border border-rose-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-lg font-black shrink-0">
                  ⏳
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-rose-600 block tracking-wider">
                    एकूण शिल्लक बाकी (Remaining Due)
                  </span>
                  <span className="text-xl font-black text-rose-700">
                    ₹{activeDoctorTotals.remaining.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-rose-600/70 block font-medium">सध्या येणे बाकी असलेली एकूण रक्कम</span>
                </div>
              </div>
            </div>

            {/* Search Filter Bar inside Popup */}
            <div className="px-6 py-2.5 bg-white border-b border-slate-100 flex items-center justify-between gap-3 shrink-0">
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  value={doctorCustomerSearch}
                  onChange={e => setDoctorCustomerSearch(e.target.value)}
                  placeholder="या डॉक्टरकडील ग्राहक नाव, फोन किंवा बिल नं शोधा..."
                  className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-teal-500 focus:bg-white transition-all"
                />
                <span className="absolute left-2.5 top-2 text-slate-400 text-xs">🔍</span>
              </div>
              <div className="text-xs font-bold text-slate-500">
                दाखवलेली ग्राहक बिले: <strong className="text-slate-800">{activeDoctorFilteredSales.length}</strong> / {activeDoctorGroup.sales.length}
              </div>
            </div>

            {/* Modal Table Body */}
            <div className="overflow-y-auto flex-1 p-4">
              {activeDoctorFilteredSales.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs font-bold">
                  या शोधानुसार कोणतेही ग्राहक किंवा बिले आढळली नाहीत.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead>
                      <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-black border-b border-slate-200">
                        <th className="p-3 pl-4">दिनांक / बिल नं</th>
                        <th className="p-3">ग्राहक नाव</th>
                        <th className="p-3">मोबाईल</th>
                        <th className="p-3 text-center">पेमेंट पद्धत</th>
                        <th className="p-3 text-right">एकूण बिल</th>
                        <th className="p-3 text-right text-emerald-700">जमा रक्कम</th>
                        <th className="p-3 text-right text-rose-700">शिल्लक बाकी</th>
                        <th className="p-3 text-center">स्टेटस</th>
                        <th className="p-3 text-center pr-4">ॲक्शन</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {activeDoctorFilteredSales.map((sale) => {
                        const total = getSaleTotal(sale);
                        const paid = getSalePaid(sale);
                        const remaining = getSaleRemaining(sale);
                        const isPaid = sale.paymentStatus === 'PAID' || remaining === 0;
                        const isPartial = paid > 0 && remaining > 0;

                        return (
                          <tr key={sale.id} className="hover:bg-teal-50/30 transition-colors">
                            <td className="p-3 pl-4">
                              <div className="font-bold text-slate-800">{sale.date}</div>
                              <div className="text-[10px] font-black text-slate-400">Inv #{sale.vchNo || '-'}</div>
                            </td>
                            <td className="p-3">
                              <div className="font-black text-slate-800">{sale.custName || 'Walk-in'}</div>
                              {sale.custAddr && <div className="text-[10px] text-slate-400">{sale.custAddr}</div>}
                            </td>
                            <td className="p-3 font-mono font-bold text-slate-600">
                              {sale.custMob || '-'}
                            </td>
                            <td className="p-3 text-center">
                              {renderPaymentModeBadge(sale.paymentMode, isPaid, isPartial)}
                            </td>
                            <td className="p-3 text-right font-bold text-slate-700">
                              ₹{total.toFixed(2)}
                            </td>
                            <td className="p-3 text-right font-black text-emerald-700 bg-emerald-50/30">
                              {paid > 0 ? `+₹${paid.toFixed(2)}` : '₹0.00'}
                            </td>
                            <td className="p-3 text-right font-black bg-rose-50/40">
                              <span className={remaining > 0 ? 'text-rose-700 font-black' : 'text-slate-400'}>
                                ₹{remaining.toFixed(2)}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {isPaid ? (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-black">
                                  ✓ PAID
                                </span>
                              ) : isPartial ? (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-black">
                                  ⏳ PARTIAL
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded-full text-[10px] font-black">
                                  PENDING
                                </span>
                              )}
                            </td>
                            <td className="p-3 pr-4 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                {!isPaid && (
                                  <button
                                    type="button"
                                    onClick={() => openPaymentModal(sale)}
                                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-black shadow-xs transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                                    title="उधारी जमा करा (Record Partial/Full Payment)"
                                  >
                                    <span>💰 जमा करा</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => openEditCreditModal(sale)}
                                  className="px-2.5 py-1 bg-amber-50 hover:bg-amber-600 text-amber-900 hover:text-white border border-amber-300 rounded-lg text-xs font-black transition-all flex items-center gap-1 shadow-2xs active:scale-95 cursor-pointer whitespace-nowrap"
                                  title="क्रेडिट एडिट करा (Credit Edit)"
                                >
                                  <span>✏️</span> क्रेडिट एडिट
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelectedSale(sale)}
                                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                  title="बिल तपशील पहा"
                                >
                                  👁️
                                </button>
                                {sale.custMob && !isPaid && (
                                  <button
                                    type="button"
                                    onClick={() => handleSendReminder(sale)}
                                    className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                    title="WhatsApp स्मरणपत्र पाठवा"
                                  >
                                    📱
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCreditSale(sale)}
                                  disabled={isDeletingSale}
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                  title="Delete this credit bill"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Table Footer: Total Addition Row (एकूण बेरीज पट्टी) */}
                    <tfoot>
                      <tr className="bg-slate-100 text-slate-900 font-black text-xs border-t-2 border-slate-300">
                        <td colSpan="4" className="p-3.5 pl-4 text-right uppercase tracking-wider font-black text-slate-700">
                          एकूण बेरीज (TOTAL ADDITION) - {activeDoctorFilteredSales.length} बिले:
                        </td>
                        <td className="p-3.5 text-right font-black text-slate-800">
                          ₹{activeDoctorTotals.total.toFixed(2)}
                        </td>
                        <td className="p-3.5 text-right font-black text-emerald-700 bg-emerald-100/50">
                          +₹{activeDoctorTotals.paid.toFixed(2)}
                        </td>
                        <td className="p-3.5 text-right font-black text-rose-700 bg-rose-100/50 text-sm">
                          ₹{activeDoctorTotals.remaining.toFixed(2)}
                        </td>
                        <td colSpan="2" className="p-3.5 text-center text-slate-400 font-bold text-[10px]">
                          {activeDoctorTotals.remaining === 0 ? '✓ सर्व क्लिअर' : 'बाकी रक्कम'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center shrink-0">
              <div className="text-xs font-bold text-slate-500">
                {activeDoctorTotals.remaining > 0 ? (
                  <span>
                    या डॉक्टरकडील एकूण येणे बाकी उधारी: <strong className="text-rose-700 text-sm">₹{activeDoctorTotals.remaining.toFixed(2)}</strong>
                  </span>
                ) : (
                  <span className="text-emerald-700 font-black">
                    ✓ या डॉक्टरकडील सर्व ग्राहकांची उधारी पूर्ण जमा झाली आहे!
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedDoctorGroupName(null)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white font-black text-xs rounded-xl shadow-sm transition-all cursor-pointer"
              >
                बंद करा (Close)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECEIVE PAYMENT MODAL (उधारी जमा नोंदवा - Partial / Full Pay) */}
      {paymentModalSale && (() => {
        const total = getSaleTotal(paymentModalSale);
        const alreadyPaid = getSalePaid(paymentModalSale);
        const currentRemaining = getSaleRemaining(paymentModalSale);
        const payVal = parseFloat(payNowAmount) || 0;
        const newRemaining = Math.max(0, currentRemaining - payVal);
        const newTotalPaid = alreadyPaid + payVal;

        return (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setPaymentModalSale(null)}>
            <div 
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200"
              onMouseDown={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-teal-800 to-[#1e5c5c] text-white flex justify-between items-center shadow-sm">
                <div>
                  <h3 className="text-base font-black flex items-center gap-2">
                    <span>💵</span> उधारी जमा करा (Receive Payment)
                  </h3>
                  <p className="text-[11px] text-teal-200 font-medium">Customer Credit Settlement Register</p>
                </div>
                <button 
                  onClick={() => setPaymentModalSale(null)}
                  className="text-white/80 hover:text-white text-xl leading-none p-1 rounded-full hover:bg-white/10"
                >
                  &times;
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-4 text-xs font-bold text-slate-700 bg-white">
                {/* Customer Details Pill */}
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-2xl flex justify-between items-center">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">Customer</span>
                    <span className="text-sm font-black text-slate-800">{paymentModalSale.custName || 'Walk-in Customer'}</span>
                    {paymentModalSale.custMob && <span className="text-slate-500 text-[11px] block">📞 {paymentModalSale.custMob}</span>}
                  </div>
                  {paymentModalSale.doctorName && (
                    <div className="text-right">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">Doctor</span>
                      <span className="text-xs font-black text-[#1e5c5c]">Dr. {paymentModalSale.doctorName}</span>
                    </div>
                  )}
                </div>

                {/* Amounts Breakdown */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-center">
                    <span className="text-[9px] uppercase font-bold text-slate-400 block">एकूण बिल (Total)</span>
                    <span className="text-xs font-black text-slate-700">₹{total.toFixed(2)}</span>
                  </div>
                  <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-200 text-center">
                    <span className="text-[9px] uppercase font-bold text-emerald-700 block">आधी जमा (Paid)</span>
                    <span className="text-xs font-black text-emerald-700">₹{alreadyPaid.toFixed(2)}</span>
                  </div>
                  <div className="bg-rose-50 p-2.5 rounded-xl border border-rose-200 text-center">
                    <span className="text-[9px] uppercase font-bold text-rose-700 block">शिल्लक बाकी (Due)</span>
                    <span className="text-sm font-black text-rose-700">₹{currentRemaining.toFixed(2)}</span>
                  </div>
                </div>

                {/* Amount to Pay Input */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex justify-between items-center">
                    <label className="text-slate-700 font-black text-xs">आता जमा रक्कम (Amount Paying Now):</label>
                    <div className="flex gap-1.5">
                      <button 
                        type="button" 
                        onClick={() => setPayNowAmount(String(currentRemaining))}
                        className="px-2 py-0.5 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded text-[10px] font-black transition-colors"
                      >
                        Full (₹{currentRemaining.toFixed(0)})
                      </button>
                      {currentRemaining > 100 && (
                        <button 
                          type="button" 
                          onClick={() => setPayNowAmount(String(Math.round(currentRemaining / 2)))}
                          className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-black transition-colors"
                        >
                          50% (₹{Math.round(currentRemaining / 2)})
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400 font-bold text-sm">₹</span>
                    <input 
                      type="number"
                      step="any"
                      autoFocus
                      value={payNowAmount}
                      onChange={e => setPayNowAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 bg-yellow-50/60 border-2 border-teal-500 rounded-xl text-lg font-black text-slate-800 outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </div>
                </div>

                {/* Payment Mode Selector */}
                <div className="space-y-1.5">
                  <label className="text-slate-700 font-black text-xs block">पेमेंट पद्धत (Payment Type / Mode):</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'CASH', label: '💵 Cash', hint: 'रोख' },
                      { id: 'ONLINE', label: '📱 Online / UPI', hint: 'GPay/PhonePe' },
                      { id: 'CARD', label: '💳 Card', hint: 'Debit/Credit' },
                      { id: 'CHEQUE', label: '🏦 Cheque', hint: 'Bank' }
                    ].map(mode => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setPaymentModeInput(mode.id)}
                        className={`p-2 rounded-xl text-center border transition-all ${
                          paymentModeInput === mode.id 
                            ? 'bg-teal-700 text-white border-teal-800 shadow-md ring-2 ring-teal-300' 
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <div className="text-xs font-black">{mode.label}</div>
                        <div className={`text-[9px] ${paymentModeInput === mode.id ? 'text-teal-200' : 'text-slate-400'}`}>{mode.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date & Reference Note */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-500 font-bold text-[11px] block mb-1">पेमेंट तारीख (Payment Date):</label>
                    <input 
                      type="date"
                      value={paymentDate}
                      onChange={e => setPaymentDate(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-800 outline-none focus:border-teal-500 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-slate-500 font-bold text-[11px] block mb-1">नोंद / Ref No (Note):</label>
                    <input 
                      type="text"
                      value={paymentNote}
                      onChange={e => setPaymentNote(e.target.value)}
                      placeholder="e.g. UTR / Receipt No / Note"
                      className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-800 outline-none focus:border-teal-500 text-xs"
                    />
                  </div>
                </div>

                {/* Live Preview of Changes */}
                <div className="p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl border border-teal-200 flex justify-between items-center">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-teal-800 block">एकूण जमा होईल (New Paid)</span>
                    <span className="text-xs font-black text-emerald-800">₹{newTotalPaid.toFixed(2)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] uppercase font-bold text-rose-700 block">या पेमेंटनंतर शिल्लक बाकी</span>
                    <span className={`text-sm font-black ${newRemaining === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {newRemaining === 0 ? '✓ पूर्ण क्लिअर (₹0.00)' : `₹${newRemaining.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentModalSale(null)}
                  disabled={isSubmittingPayment}
                  className="px-5 py-2 rounded-xl text-slate-600 bg-slate-200 hover:bg-slate-300 font-bold text-xs transition-colors"
                >
                  रद्द करा (Cancel)
                </button>
                <button
                  type="button"
                  onClick={handleSavePayment}
                  disabled={isSubmittingPayment}
                  className="px-6 py-2 rounded-xl text-white bg-[#1e5c5c] hover:bg-[#154242] font-black text-xs shadow-md transition-all flex items-center gap-1.5 active:scale-95"
                >
                  {isSubmittingPayment ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span>✓</span>
                  )}
                  पेमेंट जमा करा (Save Payment)
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sale Details Modal */}
      {selectedSale && (() => {
        const total = getSaleTotal(selectedSale);
        const paid = getSalePaid(selectedSale);
        const remaining = getSaleRemaining(selectedSale);
        const isPaid = selectedSale.paymentStatus === 'PAID' || remaining === 0;

        return (
          <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-teal-50 via-slate-50 to-white shrink-0">
                <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                  <div className="w-2.5 h-6 rounded-full bg-[#1e5c5c]"></div>
                  Invoice Details {selectedSale.vchNo && `#${selectedSale.vchNo}`}
                </h3>
                <button onClick={() => setSelectedSale(null)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                
                {/* Info Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Customer</p>
                    <p className="font-black text-slate-800 text-sm">{selectedSale.custName || 'Walk-in'}</p>
                    <p className="text-xs font-bold text-slate-500">{selectedSale.custMob || 'No Mobile'}</p>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Doctor</p>
                    <p className="font-black text-[#1e5c5c] text-sm">👨‍⚕️ {selectedSale.doctorName || 'Self / Direct'}</p>
                    <p className="text-xs font-bold text-slate-500">{selectedSale.doctorArea || selectedSale.doctorMob || ''}</p>
                  </div>

                  <div className="bg-emerald-50 p-3 rounded-2xl border border-emerald-100 shadow-xs">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-1">Paid Amount (जमा)</p>
                    <p className="font-black text-emerald-700 text-lg">₹{paid.toFixed(2)}</p>
                    <div className="mt-1">{renderPaymentModeBadge(selectedSale.paymentMode, isPaid, paid > 0 && !isPaid)}</div>
                  </div>

                  <div className="bg-rose-50 p-3 rounded-2xl border border-rose-100 shadow-xs">
                    <p className="text-[10px] font-bold text-rose-700 uppercase tracking-widest mb-1">Pending Due (शिल्लक)</p>
                    <p className="font-black text-rose-700 text-lg">₹{remaining.toFixed(2)}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mt-1 ${isPaid ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                      {isPaid ? '✓ PAID / CLEARED' : remaining < total ? 'PARTIAL DUE' : 'FULL DUE'}
                    </span>
                  </div>
                </div>

                {/* Payment History if exists */}
                {selectedSale.paymentHistory && selectedSale.paymentHistory.length > 0 && (
                  <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
                    <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <span>📜</span> जमा केलेल्या हप्त्यांचा इतिहास (Payment History):
                    </h4>
                    <div className="space-y-1.5">
                      {selectedSale.paymentHistory.map((h, hIdx) => (
                        <div key={hIdx} className="flex justify-between items-center bg-white px-3 py-2 rounded-xl border border-slate-200 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-slate-700">{h.date}</span>
                            <span className="text-[10px] font-bold bg-teal-50 text-teal-800 px-2 py-0.5 rounded border border-teal-200">
                              {h.mode || 'CASH'}
                            </span>
                            {h.note && <span className="text-slate-500 italic">({h.note})</span>}
                          </div>
                          <span className="font-black text-emerald-700">+₹{parseFloat(h.amount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products Table */}
                <div>
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                    Products List
                    <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[9px]">{selectedSale.items?.length || 0}</span>
                  </h4>
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                          <th className="p-3 border-b border-slate-200">Product Name</th>
                          <th className="p-3 border-b border-slate-200">Company</th>
                          <th className="p-3 border-b border-slate-200 text-center">Qty</th>
                          <th className="p-3 border-b border-slate-200 text-right">MRP</th>
                          <th className="p-3 border-b border-slate-200 text-right">Rate</th>
                          <th className="p-3 border-b border-slate-200 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedSale.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 text-xs font-black text-slate-700">{item.productName}</td>
                            <td className="p-3 text-[10px] font-bold text-slate-500">{item.company || '-'}</td>
                            <td className="p-3 text-xs font-black text-slate-700 text-center bg-slate-50/50">{item.qty}</td>
                            <td className="p-3 text-xs font-bold text-slate-500 text-right">{parseFloat(item.mrp || 0).toFixed(2)}</td>
                            <td className="p-3 text-xs font-bold text-slate-600 text-right">{parseFloat(item.rate || 0).toFixed(2)}</td>
                            <td className="p-3 text-sm font-black text-emerald-600 text-right">{parseFloat(item.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        {!selectedSale.items?.length && (
                          <tr><td colSpan="6" className="p-4 text-center text-xs text-slate-400 font-bold">No products found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
                 <div className="text-xs font-bold text-slate-500 flex items-center gap-3">
                   <span>Gross: <span className="text-slate-700">₹{parseFloat(selectedSale.grossAmount || 0).toFixed(2)}</span></span>
                   <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                   <span>Total Net: <span className="text-slate-800 font-black">₹{total.toFixed(2)}</span></span>
                 </div>
                 <div className="flex items-center gap-3">
                   <button
                     type="button"
                     onClick={() => {
                       const s = selectedSale;
                       setSelectedSale(null);
                       openEditCreditModal(s);
                     }}
                     className="px-3.5 py-2 bg-amber-50 hover:bg-amber-600 text-amber-900 hover:text-white font-black text-xs rounded-xl border border-amber-300 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                     title="क्रेडिट तपशील एडिट करा किंवा आणखी उधारी जोडा (Credit Edit)"
                   >
                     <span>✏️</span> क्रेडिट एडिट करा
                   </button>
                   <button
                     type="button"
                     onClick={() => handleDeleteCreditSale(selectedSale)}
                     disabled={isDeletingSale}
                     className="px-3.5 py-2 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white font-bold text-xs rounded-xl border border-rose-200 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                     title="Delete this credit bill"
                   >
                     <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                     </svg>
                     <span>{isDeletingSale ? 'Deleting...' : 'Delete Bill'}</span>
                   </button>
                   {!isPaid && (
                     <>
                       <button
                         onClick={() => openPaymentModal(selectedSale)}
                         className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5"
                       >
                         <span>💵</span> उधारी जमा करा (Receive Payment)
                       </button>
                       <button
                         onClick={() => {
                           handleClearFee(selectedSale.id, selectedSale.custName, remaining);
                           setSelectedSale(null);
                         }}
                         className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all"
                       >
                         ✓ Mark Fully Paid
                       </button>
                     </>
                   )}
                   <div className="text-sm font-black text-slate-800">
                     शिल्लक बाकी: <span className={`text-xl ml-1 font-black ${isPaid ? 'text-emerald-600' : 'text-rose-700'}`}>₹{remaining.toFixed(2)}</span>
                   </div>
                 </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* EDIT CREDIT MODAL (क्रेडिट एडिट / उधारी दुरुस्ती व बदल) */}
      {editCreditModalSale && (() => {
        const baseTotal = parseFloat(editTotalAmount) || 0;
        const extraCredit = parseFloat(editExtraCredit) || 0;
        const effectiveTotal = Math.max(0, baseTotal + extraCredit);
        const paidVal = Math.max(0, parseFloat(editPaidAmount) || 0);
        const effectivePaid = Math.min(paidVal, effectiveTotal);
        const effectiveRemaining = Math.max(0, effectiveTotal - effectivePaid);

        return (
          <div 
            className="fixed inset-0 z-[280] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
            onMouseDown={() => setEditCreditModalSale(null)}
          >
            <div 
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200 flex flex-col max-h-[92vh]"
              onMouseDown={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white p-4 sm:p-5 flex justify-between items-start shrink-0 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-xl shadow-inner border border-white/20">
                    ✏️
                  </div>
                  <div>
                    <h3 className="text-lg font-black tracking-tight">क्रेडिट एडिट करा (Edit Credit)</h3>
                    <p className="text-xs text-blue-100 font-medium">
                      बिल क्र: #{editCreditModalSale.vchNo || editCreditModalSale.invoiceNo || editCreditModalSale.id?.toString().slice(-6) || '-'} • ग्राहक उधारी दुरुस्ती
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setEditCreditModalSale(null)} 
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-lg"
                >
                  &times;
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleSaveEditCredit} className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1">
                {/* Customer & Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                      Customer Name (ग्राहकाचे नाव) *
                    </label>
                    <input 
                      type="text" 
                      list="edit-credit-cust-list"
                      required
                      value={editCustName} 
                      onChange={e => {
                        const val = e.target.value;
                        setEditCustName(val);
                        const match = knownCustomers.find(c => c.name.toLowerCase() === val.toLowerCase().trim());
                        if (match && match.mob && !editCustMob) {
                          setEditCustMob(match.mob);
                        }
                      }}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl px-3 py-2 text-xs font-black text-slate-800 outline-none transition-all uppercase"
                      placeholder="Customer Name"
                    />
                    <datalist id="edit-credit-cust-list">
                      {knownCustomers.map((c, i) => (
                        <option key={i} value={c.name}>{c.name} {c.mob ? `(${c.mob})` : ''}</option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                      Mobile Number (मोबाईल)
                    </label>
                    <input 
                      type="tel" 
                      value={editCustMob} 
                      onChange={e => setEditCustMob(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl px-3 py-2 text-xs font-black text-slate-800 outline-none transition-all"
                      placeholder="10 digit mobile"
                    />
                  </div>
                </div>

                {/* Doctor & Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                      Doctor Name (डॉक्टर)
                    </label>
                    <input 
                      type="text" 
                      list="edit-credit-doc-list"
                      value={editDoctorName} 
                      onChange={e => setEditDoctorName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none transition-all uppercase"
                      placeholder="Self / Direct"
                    />
                    <datalist id="edit-credit-doc-list">
                      {knownDoctors.map((d, i) => (
                        <option key={i} value={d}>{d}</option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                      Bill Date (तारीख)
                    </label>
                    <input 
                      type="date" 
                      value={editDate} 
                      onChange={e => setEditDate(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl px-3 py-2 text-xs font-black text-slate-800 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Financial Amounts Calculation Card */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 p-4 rounded-2xl border border-blue-200/70 space-y-3">
                  <div className="text-[11px] font-black uppercase tracking-wider text-blue-900 border-b border-blue-200/60 pb-1 flex justify-between items-center">
                    <span>उधारी कॅल्क्युलेटर (Credit Amounts)</span>
                    <span className="text-[10px] text-blue-600 font-bold normal-case">आपोआप हिशोब होतो</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-0.5">
                        Current Total Bill (चालू एकूण बिल ₹) *
                      </label>
                      <input 
                        type="number" 
                        step="any"
                        min="0"
                        required
                        value={editTotalAmount} 
                        onChange={e => setEditTotalAmount(e.target.value)}
                        className="w-full bg-white border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 rounded-lg px-2.5 py-1.5 text-xs font-black text-slate-900 text-right outline-none"
                      />
                    </div>

                    <div className="bg-emerald-50/80 p-2 rounded-xl border border-emerald-300">
                      <label className="block text-[10px] font-black text-emerald-900 mb-0.5 flex items-center justify-between">
                        <span>➕ आणखी उधारी जोडा (+ Add Credit)</span>
                        <span className="text-[9px] text-emerald-700 font-bold">नवीन उधारी</span>
                      </label>
                      <input 
                        type="number" 
                        step="any"
                        min="0"
                        value={editExtraCredit} 
                        onChange={e => setEditExtraCredit(e.target.value)}
                        placeholder="+0.00"
                        className="w-full bg-white border border-emerald-400 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-300 rounded-lg px-2.5 py-1 text-xs font-black text-emerald-900 text-right outline-none placeholder:text-emerald-300"
                      />
                      {extraCredit > 0 && (
                        <div className="text-[10px] font-bold text-emerald-700 mt-1">
                          ✓ ₹{extraCredit.toFixed(2)} अधिक उधारी जोडली जाईल.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary row */}
                  <div className="grid grid-cols-3 gap-2 pt-1 border-t border-blue-200/60 text-center">
                    <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-2xs">
                      <div className="text-[9px] font-bold uppercase text-slate-400">नवीन एकूण बिल</div>
                      <div className="text-xs font-black text-slate-800 mt-0.5">₹{effectiveTotal.toFixed(2)}</div>
                    </div>

                    <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-2xs">
                      <label className="block text-[9px] font-bold uppercase text-slate-400">Paid (जमा रक्कम ₹)</label>
                      <input 
                        type="number" 
                        step="any"
                        min="0"
                        value={editPaidAmount} 
                        onChange={e => setEditPaidAmount(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded px-1.5 py-0.5 text-xs font-black text-emerald-700 text-center outline-none focus:bg-white"
                      />
                    </div>

                    <div className={`p-2 rounded-xl border shadow-2xs ${effectiveRemaining > 0 ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
                      <div className="text-[9px] font-bold uppercase">बाकी उधारी (Due)</div>
                      <div className="text-xs font-black mt-0.5">₹{effectiveRemaining.toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                {/* Note / Narration */}
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                    Notes / Remarks (नोंद / कारण)
                  </label>
                  <textarea 
                    value={editNote} 
                    onChange={e => setEditNote(e.target.value)}
                    rows={2}
                    className="w-full bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none transition-all resize-none"
                    placeholder="उदा. औषधे दिली, नवीन उधारी जोडली किंवा जमा पावती तपशील..."
                  />
                </div>

                {/* Footer Buttons */}
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-slate-200">
                  <button 
                    type="button" 
                    onClick={() => {
                      const saleToEdit = editCreditModalSale;
                      setEditCreditModalSale(null);
                      handleEditInSales(saleToEdit);
                    }}
                    className="w-full sm:w-auto px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    title="बिलामधील औषधे, प्रमाण, दर बदलण्यासाठी सेल्स स्क्रीन उघडा"
                  >
                    <span>🧾</span> सेल्स बिलामध्ये औषधे एडिट करा
                  </button>

                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <button 
                      type="button" 
                      onClick={() => setEditCreditModalSale(null)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                    >
                      रद्द करा
                    </button>

                    <button 
                      type="submit" 
                      disabled={isSubmittingEdit}
                      className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-black shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                    >
                      {isSubmittingEdit ? 'अपडेट करत आहे...' : '💾 उधारी अपडेट करा'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PendingCredit;
